import { Component, createRef } from 'react';
import propTypes from 'prop-types';
import AssetPickerTypes from '~/components/common/asset-picker/asset-picker-types';
import AssetPickerEditFolderOptions from './edit-options/asset-picker-edit-folder-options';
import AssetPickerFolderChildren from './asset-picker-folder-children';
import StyledIconTextPair from './StyledIconTextPair';

class AssetPickerFolder extends Component {
  constructor(props) {
    super(props);
    this.folderRef = createRef();
  }

  componentDidMount() {
    const { folder, setExpanded, refreshSortable, isActive } = this.props;

    // Scroll into view if this folder is active when mounted
    if (isActive && this.folderRef.current) {
      setTimeout(() => {
        this.scrollIntoView();
      }, 100); // Small delay to ensure DOM is ready
    }

    if (!refreshSortable) {
      return;
    }

    let timeout;

    $(this.folderRef.current).droppable({
      over: (e, ui) => {
        if (this.isExpanded()) {
          return;
        }

        timeout = setTimeout(() => {
          setExpanded(folder.id, true);
          refreshSortable(ui.draggable);
        }, 1000);
      },
      out: () => {
        clearTimeout(timeout);
      },
    });
  }

  componentWillUnmount() {
    clearTimeout(this.timeout);
  }

  componentDidUpdate(prevProps) {
    // Scroll into view when folder becomes active
    if (!prevProps.isActive && this.props.isActive && this.folderRef.current) {
      this.scrollIntoView();
    }
    // Also scroll when the timestamp changes and this folder is active
    // This handles the case where the user re-selected the same item to scroll to it again to
    // locate it again in the tree. So we do a neat trick where we update the timestamp to trigger
    // a re-render which effectively causes the tree to scroll to the selected item
    else if (
      this.props.isActive &&
      this.props.currentItemTimestamp &&
      prevProps.currentItemTimestamp !== this.props.currentItemTimestamp &&
      this.folderRef.current
    ) {
      this.scrollIntoView();
    }
  }

  scrollIntoView() {
    // Use scrollIntoView with smooth behavior for better UX
    this.folderRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }

  render() {
    const { isEditable, folder, parentEditors, updateCallback, isActive, assetsAreLinks } = this.props;

    const options = isEditable ? (
      <AssetPickerEditFolderOptions folder={folder} parentEditors={parentEditors} updateCallback={updateCallback} />
    ) : (
      ''
    );
    const moveIcon = isEditable ? (
      <img
        className="asset-picker-move-icon allow-draggable"
        src="/images/move_hover_icon.svg"
        width="9px"
        height="13px"
      />
    ) : (
      ''
    );

    const childrenProps = { ...this.props };
    const children = this.isExpanded() ? (
      <AssetPickerFolderChildren {...childrenProps} assetsAreLinks={assetsAreLinks} />
    ) : (
      ''
    );
    const folderIconUrl = this.isExpanded() ? '/images/folder_icon_opened_3.svg' : '/images/folder_icon_3.svg';

    const activeClass = isActive ? 'asset-picker-asset--active' : '';
    const name = folder.displayName.trim();

    return (
      <li
        className={`asset-picker-asset asset-picker-folder ${activeClass}`}
        data-id={folder.id}
        data-type={AssetPickerTypes.FOLDER}
        data-order={folder.order}
        data-parent-id={folder.itemFolderId}
        ref={this.folderRef}
      >
        <div className="asset-picker-asset--header" ref={(folderName) => (this.folderName = folderName)}>
          {moveIcon}
          <div className="asset-picker-asset--label" onClick={this.handleFolderClick}>
            <StyledIconTextPair
              imgsrc={folderIconUrl}
              icon={this.isExpanded() ? 'folder-open' : 'folder-closed'}
              isActive={isActive}
              text={name}
              useAssetPickerTextClass
              iconClassName="asset-picker-asset--icon"
              onPress={this.handleFolderClick}
            />
          </div>
          {options}
        </div>
        {children}
      </li>
    );
  }

  handleFolderClick = () => {
    const assetCallbacks = this.props.selectableAssets;
    const callback = assetCallbacks[AssetPickerTypes.FOLDER];
    if (callback) callback(this.props.folder);

    this.toggleExpanded();
  };

  toggleExpanded = () => {
    const isExpanded = !this.isExpanded();
    if (typeof this.props.setExpanded === 'function') this.props.setExpanded(this.props.folder.id, isExpanded);
  };

  isExpanded = () => this.props.expandedFolderIds && this.props.expandedFolderIds.includes(this.props.folder.id);
}

AssetPickerFolder.propTypes = {
  isActive: propTypes.bool,
  isEditable: propTypes.bool,
  assetsAreLinks: propTypes.bool,
  updateCallback: propTypes.func.isRequired,
  setExpanded: propTypes.func.isRequired,
  selectableAssets: propTypes.objectOf(propTypes.func.isRequired),
  expandedFolderIds: propTypes.arrayOf(propTypes.string),
  folder: propTypes.shape({
    id: propTypes.string.isRequired,
    order: propTypes.number.isRequired,
    itemFolderId: propTypes.string,
    displayName: propTypes.string.isRequired,
  }).isRequired,
  parentEditors: propTypes.array.isRequired,
  refreshSortable: propTypes.func,
  excludedAssetIds: propTypes.arrayOf(propTypes.string),
  currentItemTimestamp: propTypes.string,
};

export default AssetPickerFolder;
